import type { FieldMeta } from 'vee-validate';
import type { DropdownPlacementType } from '../../Dropdown/Dropdown.types';
import type { FieldSizeType } from '../FormField';

export const SelectSelection = {
  Single: 'single',
  Multiple: 'multiple',
} as const;

export type SelectSelectionType =
  (typeof SelectSelection)[keyof typeof SelectSelection];

export const SelectCommit = {
  Immediate: 'immediate',
  Apply: 'apply',
} as const;

export type SelectCommitType = (typeof SelectCommit)[keyof typeof SelectCommit];

export const SelectOptionsAs = {
  Plain: 'plain',
  Checkbox: 'checkbox',
} as const;

export type SelectOptionsAsType =
  (typeof SelectOptionsAs)[keyof typeof SelectOptionsAs];

export type SelectOption = string | number | Record<string, unknown>;

export type SelectModelValue = SelectOption | SelectOption[] | null | undefined;

export type SelectNavTarget =
  | { kind: 'create' }
  | { kind: 'selectAll' }
  | { kind: 'option'; option: SelectOption };

export type SelectProps = {
  id?: string;
  name: string;
  label?: string;
  optionalLabel?: boolean;
  helperText?: string;
  error?: string;
  warning?: string;
  meta?: FieldMeta<unknown>;
  testId?: string;

  options: SelectOption[];
  modelValue?: SelectModelValue;
  optionLabel?: string | ((option: SelectOption) => string);
  optionValue?: string | ((option: SelectOption) => string | number);
  optionDisabled?: string | ((option: SelectOption) => boolean);

  selection?: SelectSelectionType;
  commit?: SelectCommitType;
  optionsAs?: SelectOptionsAsType;
  withSelectAll?: boolean;

  searchable?: boolean;
  internalSearch?: boolean;
  allowEmpty?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  loading?: boolean;
  placeholder?: string;
  maxVisibleTags?: number;

  groupValues?: string;
  groupLabel?: string;
  groupSelect?: boolean;

  creatable?: boolean;
  createValidator?: (query: string) => boolean;
  createLabel?: string;

  canLoadMore?: () => boolean;
  onLoadMore?: () => void | Promise<void>;
  paginationLoading?: boolean;

  placement?: DropdownPlacementType;
  disableTeleport?: boolean;
  emptyMessage?: string;
  noResultsMessage?: string;
  size?: FieldSizeType;
};

export type SelectFieldProps = SelectProps & {
  rules?: (value: SelectModelValue) => string | boolean;
};

export type SelectEmits = {
  'update:modelValue': [value: SelectModelValue];
  'update:open': [open: boolean];
  search: [query: string];
  create: [query: string];
  blur: [ev?: FocusEvent];
  loadMoreError: [error: unknown];
};
